import { trigger, state, style, transition, animate } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { AppService, GRSSResponse, GRSSUserRoles } from 'src/app/_services/app.service';
import { AddVendorComponent } from './add-vendor/add-vendor.component';

@Component({
    selector: 'app-maintenance',
    templateUrl: './maintenance.component.html',
    styleUrls: ['./maintenance.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class MaintenanceComponent implements OnInit {

  reIndexDatetimePicker: DateTimePicker;
  restartAgentDatetimePicker: DateTimePicker;

  maintenance: Maintenance;

  displayColumns: string[] = ['vendor', 'active', 'actions'];
  vendorsLength: number = 0;

  vendorlist: Vendor[] = null;
  isLoading: boolean = false;

  constructor(public service: AppService, private http: HttpClient, @Inject('BASE_URL') private baseUrl: string, private dialog: MatDialog, private translate: TranslateService) {
    this.service.SetTranslation('administration/maintenance');
  }

  ngOnInit() {
    this.InitializeProperties();
    this.GetMaintenanceDetails();
    this.GetRemoteVendors();
  }

  /** Gets the current maintenance details */
  GetMaintenanceDetails() {
    this.http.get<Maintenance>(this.baseUrl + 'api/maintenance/getMaintenanceInfo').subscribe(result => {
      this.maintenance = result;
    }, error => {
      console.error(error);
    });
  }

  /** Gets all the remote vendors */
  GetRemoteVendors() {
    this.isLoading = true;
    this.http.get<Vendor[]>(this.baseUrl + 'api/maintenance/getRemoteVendors').subscribe(result => {
      this.vendorlist = result;
      this.vendorsLength = result.length;
      if (this.service.currentUser.userRole === GRSSUserRoles.Compliance) {
        if (this.displayColumns.includes('actions')) {
          const index = this.displayColumns.indexOf('actions');
          this.displayColumns.splice(index, 1);
        }
      }
    }, error => {
      console.error(error);
    }, () => {
      this.isLoading = false;
    });
  }

  /** Initiualizes the properties used in the page */
  InitializeProperties() {
    this.InitializeReIndex();
    this.InitializeRestartAgent();

    this.maintenance = {
      reIndexParagraph: '',
      restartAgentParagraph: ''
    }
  }

  /** Initializes the reindex */
  InitializeReIndex() {
    this.reIndexDatetimePicker = {
      date: '',
      datetime: '',
      time: ''
    };
  }

  /** Initializes the restart */
  InitializeRestartAgent() {
    this.restartAgentDatetimePicker = {
      date: '',
      datetime: '',
      time: ''
    }
  }

  /**
   * Setting the correct format of the date and time
   * @param datetimePicker Selected datetime
   */
  OnChangedDatetime(datetimePicker: DateTimePicker) {
    const time: string = datetimePicker.time;
    const date: string = datetimePicker.date;


    if (datetimePicker.time === '') {
      datetimePicker.time = moment(new Date()).format('D MMM YYYY ') + '12:00 AM';
    } else {
      datetimePicker.time = moment(new Date()).format('D MMM YYYY ') + datetimePicker.time;
    }

    if (datetimePicker.date === '') {
      datetimePicker.date = moment(new Date()).format('D MMM YYYY');
    } else {
      datetimePicker.date = moment(new Date(datetimePicker.date)).format('D MMM YYYY');
    }

    datetimePicker.datetime = datetimePicker.date + ' ' + moment(new Date(datetimePicker.time)).format('LT');

    datetimePicker.time = time;
    datetimePicker.date = date;
  }

  /**
   * Apply button event
   * @param from 
   */
  OnClickApply(from: string) {
    if (from === 'reindex') {
      this.service.showSpinner();
        const arrayItems: string[] = [this.reIndexDatetimePicker.datetime]
        this.http.post<GRSSResponse>(this.baseUrl + 'api/maintenance/reIndexDb', arrayItems).subscribe(result => {
          if (result.statusCode === 4) {
            this.service.SaveStatus(true, 'Maintenance.header', result.statusMessage);
            // this.service.SaveStatus(true, 'System Maintenance', result.statusMessage);
          } else {
            this.InitializeReIndex();
            this.GetMaintenanceDetails();
            this.service.SaveStatus(false, 'Maintenance.header', result.statusMessage);
            // this.service.SaveStatus(false, 'System Maintenance', result.statusMessage);
          }
        }, error => {
          console.error(error);
        }).add(() => {
          this.service.hideSpinner();
        });
    } else if (from === 'restart') {
      this.service.showSpinner();
        const arrayItems: string[] = [this.restartAgentDatetimePicker.datetime]
        this.http.post<GRSSResponse>(this.baseUrl + 'api/maintenance/restartAgent', arrayItems).subscribe(result => {
          if (result.statusCode === 4) {
            this.service.SaveStatus(true, 'Maintenance.header', result.statusMessage);
            // this.service.SaveStatus(true, 'System Maintenance', result.statusMessage);
          } else {
            this.InitializeRestartAgent();
            this.GetMaintenanceDetails();
            this.service.SaveStatus(false, 'Maintenance.header', result.statusMessage);
            // this.service.SaveStatus(false, 'System Maintenance', result.statusMessage);
          }
        }, error => {
          console.error(error);
        }).add(() => {
          this.service.hideSpinner();
        });
    }
  }

  /**
   * Edit icon event for the vendor
   * @param vendor Selected vendor
   */
  OnEditVendor(vendor: Vendor) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = vendor;
    dialogConfig.position = { top: '5%' };
    this.dialog.open(AddVendorComponent, dialogConfig).afterClosed().subscribe((res: Vendor) => {
      if (res !== null) {
        const index = this.vendorlist.indexOf(vendor, 0);
        this.vendorlist.splice(index, 1, res);
      }
    });
  }

  /** Save event for the vendor */
  OnSaveVendors() {
    this.service.showSpinner();
    this.http.post<GRSSResponse>(this.baseUrl + 'api/maintenance/updateVendors', this.vendorlist).subscribe(result => {
      if (result.statusCode === 4) {
        this.service.SaveStatus(true, 'Maintenance.header', result.statusMessage);
        // this.service.SaveStatus(true, 'System Maintenance', result.statusMessage);
      } else {
        this.service.SaveStatus(false, 'Maintenance.header', result.statusMessage);
        // this.service.SaveStatus(false, 'System Maintenance', result.statusMessage);
        this.GetRemoteVendors();
      }
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
    });
  }
}

/** An interface to access date time picker data */
interface DateTimePicker {
  datetime: string;
  date: string;
  time: string;
}

/** An interface to access maintenance details */
interface Maintenance {
  reIndexParagraph: string;
  restartAgentParagraph: string;
}

/** An interface to access endor details */
interface Vendor {
  id: string;
  name: string;
  enabled: boolean;
  publicationMarkets: PublicationMarket[];
}

/** An interface to access publication market details */
interface PublicationMarket {
  marketId: string;
  marketName: string;
  delay: number;
  enabled: boolean;
}
